import {useFormatCurrency} from '@aboutyou/i18n';
import React, {FC, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {TextInput} from '../../../../atoms/TextInput/TextInput';

interface Props {
  label?: string;
  max: number;
  min: number;
  values: [number, number];
  onChange: (range: [number, number]) => void;
  onApply: (range: [number, number]) => void;
  className?: string;
  currencyCode: string;
  enableFilterFlyout?: boolean;
}

export const PriceRangeManualInput: FC<Props> = ({
  label,
  max,
  min,
  values,
  onChange,
  onApply,
  className,
  currencyCode,
  enableFilterFlyout,
}) => {
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);

  const formatCurrency = useFormatCurrency();
  const formatValue = (value: number) =>
    formatCurrency(value, currencyCode, {
      withoutDecimal: true,
    });

  const [inputPlaceholders, setInputPlaceholders] = useState(['', '']);

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
    setInputPlaceholders([formatValue(values[0]), formatValue(values[1])]);
  }, [values.toString()]);

  const applyChanges = (inputValue: string, inputRef: 'min' | 'max') => {
    const normalizedValues = normalizeValues(
      inputValue,
      values,
      min,
      max,
      inputRef,
    );

    if (!normalizedValues) {
      return;
    }

    const {newMinValue, newMaxValue} = normalizedValues;

    setInputPlaceholders([formatValue(newMinValue), formatValue(newMaxValue)]);

    onChange([newMinValue, newMaxValue]);
    onApply([newMinValue, newMaxValue]);

    if (minRef.current && maxRef.current) {
      minRef.current.value = '';
      maxRef.current.value = '';
    }
  };

  const inputProps = {
    type: 'number',
    pattern: '[0-9]*',
    activeStyle: {
      backgroundColor: COLORS.Grey4,
      borderColor: COLORS.Grey4,
    },
    acceptOnlyNumerics: true,
    forceEmptyValue: true,
  };

  return (
    <Wrapper className={className}>
      {label && !enableFilterFlyout && (
        <Label data-testid="priceFilterSectionTitle">{label}</Label>
      )}
      <StyledTextInput
        dataTestId="minimumInput"
        ref={minRef}
        inputMode={'numeric'}
        onBlur={e => applyChanges(e.target.value, 'min')}
        onPressEnter={e => applyChanges(e.currentTarget.value, 'min')}
        placeholder={inputPlaceholders[0]}
        $largerBorderRadius={enableFilterFlyout}
        {...inputProps}
      />
      <Divider>-</Divider>
      <StyledTextInput
        dataTestId="maximumInput"
        ref={maxRef}
        inputMode="numeric"
        onBlur={e => applyChanges(e.target.value, 'max')}
        onPressEnter={e => applyChanges(e.currentTarget.value, 'max')}
        placeholder={inputPlaceholders[1]}
        $largerBorderRadius={enableFilterFlyout}
        {...inputProps}
      />
    </Wrapper>
  );
};

const normalizeValues = (
  inputValue: string,
  currentValues: [number, number],
  min: number,
  max: number,
  inputRef: 'min' | 'max',
) => {
  if (inputValue === '') {
    return;
  }

  const normalizeNumber = (value: number) => Math.round(value / 100);

  min = normalizeNumber(min);
  max = normalizeNumber(max);
  currentValues[0] = normalizeNumber(currentValues[0]);
  currentValues[1] = normalizeNumber(currentValues[1]);

  let newMinValue: number;
  let newMaxValue: number;

  const isFirstInteraction =
    currentValues[0] === min && currentValues[1] === max;

  if (inputRef === 'min') {
    newMinValue = +inputValue;
    newMaxValue = currentValues[1];

    if (newMinValue === currentValues[0]) {
      return;
    }

    if (isFirstInteraction && newMinValue >= max) {
      newMinValue = min;
    } else if (newMinValue < min) {
      newMinValue = min;
    } else if (newMinValue >= currentValues[1] && newMinValue < max) {
      newMaxValue = max;
    } else if (newMinValue >= max) {
      newMinValue = currentValues[0];
    }
  } else {
    newMinValue = currentValues[0];
    newMaxValue = +inputValue;

    if (newMaxValue === currentValues[1]) {
      return;
    }

    if (isFirstInteraction && newMaxValue <= min) {
      newMaxValue = max;
    } else if (newMaxValue > max) {
      newMaxValue = max;
    } else if (newMaxValue <= currentValues[0] && newMaxValue > min) {
      newMinValue = min;
    } else if (newMaxValue <= min) {
      newMaxValue = currentValues[1];
    }
  }

  return {newMinValue: newMinValue * 100, newMaxValue: newMaxValue * 100};
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  & > * {
    flex: 1;
  }
`;

const Label = styled.div`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.bold};
  flex: 0.8;
`;

const Divider = styled.span`
  text-align: center;
  flex: 0.3;
`;

const StyledTextInput = styled(TextInput)<{$largerBorderRadius?: boolean}>`
  & input {
    ${props => (props.$largerBorderRadius ? 'border-radius: 4px;' : '')}
  }
`;
